import { Button } from '@eventbrite/eds-button';
import { Illustration } from '@eventbrite/eds-illustration';
import { Modal } from '@eventbrite/eds-modal';
import { gettext } from '@eventbrite/i18n';
import React, { useState } from 'react';
import {
    ActivationTrialBackground,
    ActivationTrialReadyIcon,
    ButtonsSecondStepProps,
    errorNotification,
    LearnMoreInformation,
    ModalFirstStepButtons,
    ModalSecondStepButtons,
    reloadOnCloseSecondStep,
    SeeAllPlansInformation,
    trackOnClose,
    trackOnOpen,
} from '../constants/ActivationTrialModal';
import './ActivationModal.scss';
import ActivationModalSkeleton from './ActivationModalSkeleton';

export interface ActivationTrialModalProps {
    buttonText: string;
    buttonStyle: string;
    buttonContainerClassName?: string;
    getStartedRedirect: string;
    amplitudeSource?: string;
    openModal: boolean;
    showButton: boolean;
    onCloseModal?: () => void;
}

export function ActivationTrialModal(props: ActivationTrialModalProps) {
    const [isOpen, setIsOpen] = useState<boolean>(props.openModal);
    const [showSecondStep, setShowSecondStep] = useState<boolean>(false);
    const [showMessageError, setShowMessageError] = useState<boolean>(false);
    const [messageError, setMessageError] = useState<string>('');

    const enableSecondStep = () => setShowSecondStep(true);
    const disableSecondStep = () => setShowSecondStep(false);

    const closeModal = () => {
        props.onCloseModal?.();
        trackOnClose(showSecondStep);
        setIsOpen(false);
        disableSecondStep();
        setShowMessageError(false);
        reloadOnCloseSecondStep(showSecondStep);
    };
    const openModal = () => {
        trackOnOpen(props.amplitudeSource);
        setIsOpen(true);
    };

    const showError = (message: string) => {
        setMessageError(message);
        setShowMessageError(true);
    };

    const NotificationError = () => {
        return showMessageError ? errorNotification(messageError) : null;
    };

    const firstStep = (
        <>
            <NotificationError />
            <ModalInviteStep
                enableSecondStep={() => enableSecondStep()}
                showError={showError}
                closeModal={() => closeModal()}
                showMessageError={showMessageError}
            />
        </>
    );

    const secondStep = (
        <ModalConfirmStep
            closeModal={() => closeModal()}
            getStartedRedirect={props.getStartedRedirect}
        />
    );

    const TrialFlow = () => {
        return showSecondStep ? secondStep : firstStep;
    };

    const renderModal = () => {
        return (
            <div className="boost-activation-modal">
                <Modal isShown={isOpen} onClose={closeModal} noPadding={true}>
                    <TrialFlow />
                </Modal>
            </div>
        );
    };

    const renderContent = () => {
        return (
            <>
                {props.showButton && (
                    <Button
                        style={props.buttonStyle}
                        onClick={() => openModal()}
                        id="open-modal-button"
                        __containerClassName={props.buttonContainerClassName}
                    >
                        {props.buttonText}
                    </Button>
                )}
                {renderModal()}
            </>
        );
    };

    return renderContent();
}

ActivationTrialModal.defaultProps = {
    buttonText: 'Start your free trial',
    buttonStyle: 'fill',
    openModal: false,
    showButton: true,
} as Partial<ActivationTrialModalProps>;

interface ModalInviteStepProps {
    enableSecondStep: Function;
    showError: Function;
    closeModal: Function;
    showMessageError: boolean;
}

function ModalInviteStep(props: ModalInviteStepProps) {
    const gifUrl =
        'https://st.toneden.io/prod-assets/images/boost/boost-welcome-20fps.gif';

    const [loading, setLoading] = useState(true);

    return (
        <div className="boost-activation-modal__content_step_1">
            {loading && !props.showMessageError ? (
                <div className="skeleton">
                    <ActivationModalSkeleton />
                </div>
            ) : null}
            <div>
                <img
                    className={loading ? 'hidden' : ''}
                    src={gifUrl}
                    onLoad={() => setLoading(false)}
                />
            </div>
            <div
                className={`boost-activation-modal__buttons_position ${
                    loading ? 'hidden' : ''
                }`}
            >
                <div className="eds-g-cell eds-g-cell-6-12">
                    <LearnMoreInformation />
                </div>
                <div className="eds-g-cell eds-g-cell-6-12">
                    <ModalFirstStepButtons {...props} />
                </div>
            </div>
        </div>
    );
}

const ModalConfirmStep = (props: ButtonsSecondStepProps) => (
    <>
        <div>
            <div className="boost-activation-modal__background">
                <Illustration
                    type={<ActivationTrialBackground />}
                    height="100%"
                    width="100%"
                />
            </div>
            <div className="boost-activation-modal__content_step_2">
                <div className="boost-activation-modal__icon_position eds-align--center-horizontal">
                    {ActivationTrialReadyIcon}
                </div>
                <div className="eds-l-mar-top-6 eds-l-pad-top-2 eds-align-center eds-text-hl">
                    <div className="eds-align--center eds-text-color--control--active">
                        {gettext('Ready. Set. Grow!')}
                    </div>
                    <div className="eds-align--center eds-text-color--secondary-brand">
                        {gettext('Your 14-day free trial starts now')}
                    </div>
                </div>
                <div className="boost-activation-modal__buttons_position">
                    <div className="boost-activation-help-text">
                        <SeeAllPlansInformation />
                    </div>
                    <div className="boost-activation-help-button"></div>
                    <div className="boost-activation-help-button">
                        <ModalSecondStepButtons {...props} />
                    </div>
                </div>
            </div>
        </div>
    </>
);
